import React, { useState } from "react";

import ReactDOM from "react-dom";
import {
  trackFeaturedMatchClick,
  trackMatchInteraction,
} from "../../../../libraries/_modules/tracking/helpers";
import { statusTypes, useMatchData } from "../../../mls-match-list/utils";
import { Link } from "../../../shared/components/Link/Link";
import MatchButton from "../../../shared/components/MatchButton/MatchButton";
import TicketModal from "../../../shared/components/TicketModal/main";
import { WatchOnAppleIcon } from "../../../shared/components/WatchOnAppleIcon/WatchOnAppleIcon";
import { useReactAppState } from "../../../shared/containers/utils";
import { backgroundTypes } from "../../../shared/styled/Theme";
import { StyledMatchButtonStreamer } from "./StyledMatchButtonStreamer";

const MatchButtonStreamer = ({
  backgroundColor = backgroundTypes.black,
  size = "100%",
}) => {
  const { t, urlList, directoryList } = useReactAppState();
  const [isOpen, setIsOpen] = useState(false);
  const {
    appleStreamURL,
    competition,
    promoInfo,
    season,
    slug,
    status,
    homeClubData,
    awayClubData,
  } = useMatchData();

  const { baseUrl } = urlList || {};
  const { matchHubDir } = directoryList || {};
  const { slug: competitionSlug } = competition || {};
  const { season: currentYear } = season || {};

  const matchPageUrl = `${baseUrl}/${matchHubDir}/${competitionSlug}/${currentYear}/matches/${slug}`;

  const homeShortName = homeClubData?.shortName;
  const awayShortName = awayClubData?.shortName;

  const getAriaLabelFromStatus = () => {
    switch (status?.abbreviation) {
      case statusTypes.pre:
        if (
          promoInfo?.firstPartyTickets?.url ||
          promoInfo?.thirdPartyTickets?.url
        ) {
          return t("fm_cta_buy_tickets")
            ?.replace("{0}", homeShortName)
            .replace("{1}", awayShortName);
        } else {
          return t("fm_cta_match_details")
            ?.replace("{0}", homeShortName)
            .replace("{1}", awayShortName);
        }
      case statusTypes.live:
        if (appleStreamURL) {
          return t("fm_cta_watch_on_atv")
            ?.replace("{0}", homeShortName)
            .replace("{1}", awayShortName);
        } else {
          return t("fm_cta_match_details")
            ?.replace("{0}", homeShortName)
            .replace("{1}", awayShortName);
        }
      case statusTypes.post:
        return t("fm_cta_match_details")
          ?.replace("{0}", homeShortName)
          .replace("{1}", awayShortName);
      default:
        return null;
    }
  };

  const getUrlFromStatus = () => {
    switch (status?.abbreviation) {
      case statusTypes.pre:
        return (
          promoInfo?.firstPartyTickets?.url ||
          promoInfo?.thirdPartyTickets?.url ||
          matchPageUrl
        );
      case statusTypes.live:
        return appleStreamURL || matchPageUrl;
      case statusTypes.post:
        return matchPageUrl;
      default:
        return null;
    }
  };

  const getCtaFromStatus = () => {
    if (
      status?.abbreviation === statusTypes.pre &&
      (promoInfo?.firstPartyTickets?.url || promoInfo?.thirdPartyTickets?.url)
    ) {
      return (
        promoInfo?.firstPartyTickets?.displayText ||
        promoInfo?.thirdPartyTickets?.displayText ||
        t("buy_tickets")
      );
    }
    if (status?.abbreviation === statusTypes.live && appleStreamURL) {
      return <WatchOnAppleIcon width={81} height={14} />;
    }
    return t("match_details");
  };

  const getLiveCta = () => {
    return `${t("match_watch_on")} ${t("appletv")}`.toLowerCase();
  };

  const getCtaForTacking = () => {
    if (status?.abbreviation === statusTypes.live && appleStreamURL) {
      return getLiveCta();
    }

    return getCtaFromStatus();
  };

  const trackButton = () => {
    trackMatchInteraction({
      clickType: "button",
      clickItem: getCtaForTacking(),
      match: slug,
      matchState: status?.abbreviation,
      competition: competition?.shortName,
    });

    trackFeaturedMatchClick({
      clickType: "match",
      clickItem: getLiveCta(),
      match: slug,
      matchState: status?.abbreviation,
      competition: competition?.shortName,
    });
  };

  if (
    status?.abbreviation === statusTypes.cancelled ||
    status?.abbreviation === statusTypes.abandoned ||
    status?.abbreviation === statusTypes.postponed
  ) {
    return null;
  }

  const shouldOpenTicketModal =
    status?.abbreviation === statusTypes.pre &&
    promoInfo?.firstPartyTickets?.url &&
    promoInfo?.thirdPartyTickets?.url;

  if (shouldOpenTicketModal) {
    return (
      <StyledMatchButtonStreamer
        backgroundColor={backgroundColor}
        className="mls-c-matchbutton-streamer"
      >
        <MatchButton
          backgroundColor={backgroundColor}
          size={size}
          onClick={() => setIsOpen(true)}
        >
          {t("buy_tickets")}
        </MatchButton>
        {ReactDOM.createPortal(
          <TicketModal isOpen={isOpen} onCloseModal={() => setIsOpen(false)} />,
          document.getElementById("react-root")
        )}
      </StyledMatchButtonStreamer>
    );
  }

  return (
    <StyledMatchButtonStreamer
      backgroundColor={backgroundColor}
      className="mls-c-matchbutton-streamer"
    >
      <Link
        url={getUrlFromStatus()}
        target={status?.abbreviation !== statusTypes.post ? "_blank" : "_self"}
        title={getAriaLabelFromStatus()}
        ariaLabel={getAriaLabelFromStatus()}
        rel="noopener noreferrer"
      >
        <MatchButton
          backgroundColor={backgroundColor}
          size={size}
          onClick={() => trackButton()}
        >
          {getCtaFromStatus()}
        </MatchButton>
      </Link>
    </StyledMatchButtonStreamer>
  );
};
export default MatchButtonStreamer;
